import React, { useState } from "react";
import { cva } from "class-variance-authority";
import { twMerge } from "tailwind-merge";

const inputClasses = cva(
  "w-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        primary: "border-border-primary focus:ring-primary-background",
        secondary: "border-border-white focus:ring-secondary-background",
        outline: "bg-transparent focus:ring-blue-500",
      },
      size: {
        small: "text-xs px-4 py-2",
        medium: "text-xs px-6 py-3",
        large: "text-sm px-8 py-4",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "medium",
    },
  },
);

const EditText = ({
  // Required parameters with defaults
  placeholder = "",
  text_font_size = "text-xs",
  text_font_family = "Poppins",
  text_font_weight = "font-normal",
  text_line_height = "leading-tight",
  text_text_align = "left",
  text_color = "text-input-text",
  border_border = "1px solid #ffffff",
  border_border_radius = "rounded-xl",

  // Optional parameters (no defaults)
  layout_width,
  padding,
  position,

  // Standard React props
  variant,
  size,
  type = "text",
  value,
  onChange,
  disabled = false,
  className,
  id,
  name,
  required = false,
  ...props
}) => {
  const [inputValue, setInputValue] = useState(value || "");

  // Safe validation for optional parameters
  const hasValidWidth =
    layout_width &&
    typeof layout_width === "string" &&
    layout_width?.trim() !== "";
  const hasValidPadding =
    padding && typeof padding === "string" && padding?.trim() !== "";
  const hasValidPosition =
    position && typeof position === "string" && position?.trim() !== "";

  // Parse padding if provided
  const parsePadding = (paddingStr) => {
    if (!paddingStr) return "";
    const parts = paddingStr?.split(",");
    let classes = [];

    parts?.forEach((part) => {
      const [side, value] = part?.split("=");
      const pixelValue = value?.replace("px", "");
      switch (side) {
        case "t":
          classes?.push(`pt-[${pixelValue}px]`);
          break;
        case "r":
          classes?.push(`pr-[${pixelValue}px]`);
          break;
        case "b":
          classes?.push(`pb-[${pixelValue}px]`);
          break;
        case "l":
          classes?.push(`pl-[${pixelValue}px]`);
          break;
      }
    });
    return classes?.join(" ");
  };

  // Build optional Tailwind classes
  const optionalClasses = [
    hasValidWidth
      ? layout_width === "flex-1"
        ? "flex-1"
        : `w-[${layout_width}]`
      : "",
    hasValidPadding ? parsePadding(padding) : "px-[30px] py-[14px]",
    hasValidPosition ? position : "",
  ]
    ?.filter(Boolean)
    ?.join(" ");

  // Handle input change
  const handleChange = (event) => {
    const newValue = event?.target?.value;
    setInputValue(newValue);
    if (typeof onChange === "function") {
      onChange(event);
    }
  };

  // Build dynamic styles
  const dynamicStyles = {
    fontFamily: text_font_family || "Poppins",
    textAlign: text_text_align || "center",
    color: text_color === "#ffffff7f" ? "rgba(255, 255, 255, 0.5)" : text_color,
    border: border_border || "1px solid #ffffff",
    backgroundColor: "transparent",
  };

  return (
    <input
      type={type}
      id={id}
      name={name}
      placeholder={placeholder}
      value={value !== undefined ? value : inputValue}
      onChange={handleChange}
      disabled={disabled}
      required={required}
      style={dynamicStyles}
      className={twMerge(
        inputClasses({ variant, size }),
        text_font_size,
        text_font_weight,
        text_line_height,
        border_border_radius,
        optionalClasses,
        "placeholder-opacity-75",
        className,
      )}
      {...props}
    />
  );
};

export default EditText;
